import React, { useRef, useEffect } from 'react';

/**
 * Props for the DualScrollTable component
 * @property {React.ReactNode} children - The table content to be rendered
 * @property {string} [width] - Fixed width for the table content (defaults to '1200px')
 * @property {string} [className] - Additional CSS classes to apply to the component
 */
interface DualScrollTableProps {
  children: React.ReactNode;
  width?: string;
  className?: string;
}

/**
 * A component that provides synchronized horizontal scrolling with both top and bottom scrollbars
 * for tables with many columns. Uses a fixed width approach to ensure consistent column widths
 * and proper horizontal scrolling.
 */
const DualScrollTable: React.FC<DualScrollTableProps> = ({
  children,
  width = '1200px',
  className = ''
}) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  // Sync scrolling between top and bottom scrollbars
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;

    if (!topScroll || !bottomScroll) return;

    const handleTopScroll = () => {
      if (bottomScroll.scrollLeft !== topScroll.scrollLeft) {
        bottomScroll.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleBottomScroll = () => {
      if (topScroll.scrollLeft !== bottomScroll.scrollLeft) {
        topScroll.scrollLeft = bottomScroll.scrollLeft;
      }
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Top scrollbar */}
      <div 
        ref={topScrollRef}
        className="h-4 overflow-x-auto overflow-y-hidden bg-white border-b border-gray-200"
        style={{ marginBottom: '-16px', position: 'sticky', top: 0, zIndex: 10 }}
      >
        <div style={{ width, height: '1px' }} />
      </div>

      {/* Table content with bottom scrollbar */}
      <div 
        ref={bottomScrollRef}
        className="overflow-x-auto mt-4"
      >
        <div style={{ width }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default DualScrollTable;
